import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { type Project, type ProjectList, projectsQuery } from "./queries";
import {
  ORGANIZATION_KEY,
  pickOrganization,
  pickProject,
  projectKey,
  readStored,
  store,
} from "./selection";
import type { Membership, SessionUser } from "./session";

/**
 * The signed-in user, the organization the console is looking at (from the session's
 * memberships) and the project inside it (from `GET /orgs/:orgId/projects`). Pages read the
 * selection through `OrgScope` and `ProjectScope` (components/page/scope.tsx), which also render
 * the states for "no organization", "no projects" and a failed load.
 */
export interface Workspace {
  user: SessionUser;
  memberships: readonly Membership[];
  organization: Membership | null;
  selectOrganization: (organizationId: string) => void;
  /** The organization's projects; null when there is no organization to ask about. */
  projects: UseQueryResult<ProjectList> | null;
  project: Project | null;
  selectProject: (projectId: string) => void;
}

const WorkspaceContext = createContext<Workspace | null>(null);

export function WorkspaceProvider({
  user,
  memberships,
  children,
}: {
  user: SessionUser;
  memberships: readonly Membership[];
  children: ReactNode;
}) {
  const [organizationId, setOrganizationId] = useState(() => readStored(ORGANIZATION_KEY));
  const [projectChoices, setProjectChoices] = useState<Readonly<Record<string, string>>>({});

  const organization = pickOrganization(memberships, organizationId);
  const orgId = organization?.organization_id ?? null;

  const projectsResult = useQuery({ ...projectsQuery(orgId ?? ""), enabled: orgId !== null });
  const projects = orgId === null ? null : projectsResult;
  const storedProject =
    orgId === null ? null : (projectChoices[orgId] ?? readStored(projectKey(orgId)));
  const project = projects?.data ? pickProject(projects.data.data, storedProject) : null;

  const selectOrganization = useCallback((id: string) => {
    setOrganizationId(id);
    store(ORGANIZATION_KEY, id);
  }, []);

  const selectProject = useCallback(
    (projectId: string) => {
      if (orgId === null) return;
      setProjectChoices((choices) => ({ ...choices, [orgId]: projectId }));
      store(projectKey(orgId), projectId);
    },
    [orgId],
  );

  const value = useMemo<Workspace>(
    () => ({
      user,
      memberships,
      organization,
      selectOrganization,
      projects,
      project,
      selectProject,
    }),
    [user, memberships, organization, selectOrganization, projects, project, selectProject],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): Workspace {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error("useWorkspace must be used inside the shell");
  return value;
}
